import React from 'react';
import { Typography } from 'antd';
import { StopOutlined, CreditCardOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

interface Props {
  cancellation: string | null;
  payment: string | null;
  lateArrival: string | null;
}

interface PolicyRowProps {
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
  title: string;
  text: string;
}

const PolicyRow: React.FC<PolicyRowProps> = ({ icon, iconColor, iconBg, title, text }) => (
  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
    <div style={{
      width: 32, height: 32, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
      background: iconBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 14, color: iconColor, marginTop: 1,
    }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <Typography.Text style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--bl-text-secondary)', marginBottom: 2 }}>
        {title}
      </Typography.Text>
      <Typography.Text style={{ fontSize: 13, color: 'var(--bl-text)', lineHeight: 1.5 }}>
        {text}
      </Typography.Text>
    </div>
  </div>
);

export const PoliciesCard: React.FC<Props> = ({ cancellation, payment, lateArrival }) => {
  const { t } = useTranslation('customer');
  if (cancellation === null && payment === null && lateArrival === null) return null;
  return (
    <div style={{
      margin: '10px 16px 0',
      borderRadius: 'var(--bl-radius-lg)',
      background: 'var(--bl-surface)',
      border: '1px solid var(--bl-divider)',
      overflow: 'hidden',
    }}>
      <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid var(--bl-divider)' }}>
        <Typography.Text style={{ fontSize: 13, fontWeight: 700, color: 'var(--bl-text)' }}>
          {t('profile.policies.title')}
        </Typography.Text>
      </div>
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {cancellation !== null && (
          <PolicyRow
            icon={<StopOutlined />}
            iconColor="rgba(239,68,68,1)"
            iconBg="rgba(239,68,68,0.10)"
            title={t('profile.policies.shortCancellation')}
            text={cancellation}
          />
        )}
        {payment !== null && (
          <PolicyRow
            icon={<CreditCardOutlined />}
            iconColor="var(--bl-primary)"
            iconBg="rgba(51,144,236,0.10)"
            title={t('profile.policies.shortPayment')}
            text={payment}
          />
        )}
        {lateArrival !== null && (
          <PolicyRow
            icon={<ClockCircleOutlined />}
            iconColor="rgba(217,119,6,1)"
            iconBg="rgba(217,119,6,0.10)"
            title={t('profile.policies.shortLateness')}
            text={lateArrival}
          />
        )}
      </div>
    </div>
  );
};
